/** Tab title "(12/40) Revive" and a progress ring on the favicon while a batch runs. */
import { useEffect, useMemo } from 'react';
import { BRAND } from '../../app/brand';
import { useApp } from '../../app/store';

const BASE_ICON = '/favicon.svg';

function iconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

/** Favicon with a progress arc (0..1) drawn around it; paused shows a grey arc. */
function badgeIcon(fraction: number, paused: boolean): string {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#b4691f';
  g.beginPath();
  g.roundRect(4, 4, 56, 56, 14);
  g.fill();
  g.lineWidth = 8;
  g.strokeStyle = 'rgba(255,255,255,.35)';
  g.beginPath();
  g.arc(32, 32, 18, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = paused ? '#d8d0c4' : '#fdf8f0';
  g.beginPath();
  g.arc(32, 32, 18, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0.04, fraction));
  g.stroke();
  return c.toDataURL('image/png');
}

/** Batch progress as a 0..1 fraction including the photo in progress. */
export function useBatchFraction(): number | null {
  const batch = useApp((s) => s.batch);
  const progress = useApp((s) => s.progress);
  return useMemo(() => {
    if (!batch || !batch.total) return null;
    const partial = Object.values(progress).reduce((s, p) => s + (p.fraction ?? 0), 0);
    return Math.min(1, (batch.done + batch.failed + partial) / batch.total);
  }, [batch, progress]);
}

export function QueueIndicators() {
  const batch = useApp((s) => s.batch);
  const paused = useApp((s) => s.queuePaused);
  const fraction = useBatchFraction();
  const active = !!batch && batch.total > 0;
  const step = fraction === null ? null : Math.round(fraction * 20);

  useEffect(() => {
    document.title = active ? `${paused ? 'Paused ' : ''}(${batch!.done + batch!.failed}/${batch!.total}) ${BRAND}` : BRAND;
  }, [active, paused, batch]);

  // Redraw the icon in 5% steps only.
  useEffect(() => {
    iconLink().href = active && step !== null ? badgeIcon(step / 20, paused) : BASE_ICON;
  }, [active, step, paused]);

  useEffect(
    () => () => {
      document.title = BRAND;
      iconLink().href = BASE_ICON;
    },
    [],
  );
  return null;
}
